/**
 * useDictation — capture mic audio for a single utterance, stream it to
 * the backend, and surface a final transcript when the user releases the
 * mic.
 *
 * State machine:
 *
 *   idle ──start()──► requesting (getUserMedia)
 *                          │
 *                          ▼
 *                       recording ──stop()────► finalizing ──onFinal──► idle
 *                          │                                              ▲
 *                          ├─cancel()──► (close WS, drop audio) ──────────┘
 *                          │
 *                          └─onError────────────────────────────────────► error
 *
 * The backend (offline Moonshine in sherpa-onnx) does not emit live
 * partials, so this hook also doesn't expose a partial string. To keep
 * the UI honest about "yes we are listening", it polls an ``AnalyserNode``
 * via ``requestAnimationFrame`` and surfaces a rolling RMS-level array
 * (``levels``) plus an ``elapsedMs`` counter — the chat overlay renders a
 * Telegram-style waveform from these.
 *
 * Sample-rate strategy: we request a 16 kHz AudioContext directly, which
 * Chromium/Firefox honour by resampling at the graph boundary. That lets
 * us avoid shipping our own resampler. A ScriptProcessorNode (deprecated
 * but universally supported) is used for capture — AudioWorklet would
 * be cleaner but needs a separate worklet module, and ScriptProcessor
 * is fine for ~16 kHz mono.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { openDictationSocket, type DictationSocket, type DictationEndContext } from "@/api/audio";

export type DictationStatus =
  | "idle"
  | "requesting"
  | "recording"
  | "finalizing"
  | "error";

/**
 * Number of bars in the rolling level meter. 32 matches Telegram's
 * recording UI and looks balanced inside the input row at ~240px wide.
 */
export const DICTATION_LEVEL_BARS = 32;

/**
 * How often we refresh the level ring (Hz). 20 is smooth without
 * thrashing React — each push triggers a state update on the rendered
 * waveform.
 */
const LEVEL_TICK_HZ = 20;

export interface UseDictationOptions {
  /**
   * Called with the decoded transcript when the user stops dictation.
   * Fires even for empty transcripts (silence / very short clip) so the
   * caller can clear any "Transcribing…" UI without special-casing.
   * ``truncated`` is true when the recording hit the backend's
   * MAX_UTTERANCE_SECONDS cap; surface it as a one-line warning.
   */
  onFinal?: (text: string, truncated: boolean) => void;
  /**
   * Specific input device chosen in the setup modal. Falls back to the
   * system default when undefined or when the device is no longer present.
   */
  deviceId?: string;
}

export interface UseDictationResult {
  status: DictationStatus;
  /**
   * Rolling RMS levels in [0, 1], length :data:`DICTATION_LEVEL_BARS`.
   * Index 0 is the oldest sample, last index is the most recent.
   * Always populated (zeros when idle) so the renderer can mount unconditionally.
   */
  levels: number[];
  /** Milliseconds since recording started; 0 when not recording. */
  elapsedMs: number;
  /** Last error message, cleared on next ``start()``. */
  error: string | null;
  start: () => Promise<void>;
  /**
   * Stop recording and request a transcript. Optionally pass the current
   * draft + recent chat turns so the backend can integrate the new
   * dictation into the box and resolve names/casing from context.
   */
  stop: (context?: DictationEndContext) => void;
  /** Abort the recording and drop the audio — no transcript fires. */
  cancel: () => void;
}

function zeroLevels(): number[] {
  return new Array(DICTATION_LEVEL_BARS).fill(0);
}

export function useDictation(opts: UseDictationOptions = {}): UseDictationResult {
  const [status, setStatus] = useState<DictationStatus>("idle");
  const [levels, setLevels] = useState<number[]>(zeroLevels);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Refs hold the audio graph + socket so we can tear them down from
  // both ``stop()`` / ``cancel()`` and the cleanup effect. They live
  // outside React state because none of them should trigger re-renders.
  const ctxRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const muteGainRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const socketRef = useRef<DictationSocket | null>(null);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  // True once stop() has been called for this session — guards against
  // a late "final" landing after the user already cancelled.
  const stopRequestedRef = useRef(false);

  const onFinalRef = useRef(opts.onFinal);
  onFinalRef.current = opts.onFinal;
  const deviceIdRef = useRef(opts.deviceId);
  deviceIdRef.current = opts.deviceId;

  const teardown = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    try {
      processorRef.current?.disconnect();
    } catch {
      /* already disconnected */
    }
    processorRef.current = null;

    try {
      analyserRef.current?.disconnect();
    } catch {
      /* already disconnected */
    }
    analyserRef.current = null;

    try {
      sourceRef.current?.disconnect();
    } catch {
      /* already disconnected */
    }
    sourceRef.current = null;

    try {
      muteGainRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    muteGainRef.current = null;

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    if (ctxRef.current && ctxRef.current.state !== "closed") {
      ctxRef.current.close().catch(() => {
        /* already closed */
      });
    }
    ctxRef.current = null;
  }, []);

  const stop = useCallback((context?: DictationEndContext) => {
    if (stopRequestedRef.current) return;
    stopRequestedRef.current = true;
    console.debug("[dictation] stop() — requesting transcript");
    setStatus((s) => (s === "recording" ? "finalizing" : s));
    socketRef.current?.end(context);
    // Drop the audio graph immediately so the mic light goes off — the
    // backend has all the audio it needs to decode at this point. The WS
    // ``close`` handler will reset state when the final frame arrives.
    teardown();
  }, [teardown]);

  const cancel = useCallback(() => {
    stopRequestedRef.current = true;
    console.debug("[dictation] cancel() — discarding recording");
    socketRef.current?.close();
    socketRef.current = null;
    teardown();
    setStatus("idle");
    setLevels(zeroLevels());
    setElapsedMs(0);
  }, [teardown]);

  const start = useCallback(async () => {
    if (status !== "idle" && status !== "error") return;
    console.debug("[dictation] start() — requesting mic, deviceId=%s", deviceIdRef.current ?? "default");
    setError(null);
    setLevels(zeroLevels());
    setElapsedMs(0);
    stopRequestedRef.current = false;
    setStatus("requesting");

    let stream: MediaStream;
    const baseAudio: MediaTrackConstraints = {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    // Try the user-selected device first; fall back to the OS default if
    // it's been unplugged since the modal closed (deviceId is invalidated
    // on disconnect).
    const preferred = deviceIdRef.current;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: preferred ? { ...baseAudio, deviceId: { exact: preferred } } : baseAudio,
        video: false,
      });
    } catch (e) {
      if (preferred) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: baseAudio, video: false });
        } catch (e2) {
          const msg = e2 instanceof Error ? e2.message : "Microphone permission denied";
          setError(msg);
          setStatus("error");
          return;
        }
      } else {
        const msg = e instanceof Error ? e.message : "Microphone permission denied";
        setError(msg);
        setStatus("error");
        return;
      }
    }

    // Request a 16 kHz graph — Chromium/Firefox honour this and resample
    // the input automatically. If it fails (rare), the fallback path
    // below uses the default sample rate; the backend can still process
    // at non-16 kHz but the recognizer is configured for 16 kHz so the
    // transcript may be lossy.
    let ctx: AudioContext;
    try {
      ctx = new AudioContext({ sampleRate: 16000 });
    } catch {
      ctx = new AudioContext();
    }
    console.debug(
      "[dictation] AudioContext sampleRate=%d, state=%s",
      ctx.sampleRate, ctx.state,
    );
    if (ctx.state === "suspended") {
      try {
        await ctx.resume();
      } catch {
        /* suspended contexts auto-resume on first node connection */
      }
    }

    // ScriptProcessorNode is deprecated but the only universally-supported
    // way to grab raw PCM frames without shipping an AudioWorklet module.
    // 4096 samples @ 16 kHz ≈ 256 ms — fine for buffered offline ASR.
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    const source = ctx.createMediaStreamSource(stream);

    // Mute output so the mic doesn't echo back through speakers; we still
    // have to connect to destination for onaudioprocess to fire.
    const muteGain = ctx.createGain();
    muteGain.gain.value = 0;

    // Parallel branch to the processor: feeds the level meter. Doesn't
    // need to connect to destination — the analyser only needs the
    // source plugged in to pull samples on demand.
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.5;

    streamRef.current = stream;
    ctxRef.current = ctx;
    processorRef.current = processor;
    sourceRef.current = source;
    muteGainRef.current = muteGain;
    analyserRef.current = analyser;

    const socket = openDictationSocket({
      onReady: () => {
        // Don't flip to "recording" if the user already cancelled while
        // the WS was warming up.
        if (!stopRequestedRef.current) {
          setStatus("recording");
          startTimeRef.current = performance.now();
        } else {
          console.debug("[dictation] ready arrived after stop/cancel — staying in finalizing");
        }
      },
      onFinal: (text, truncated) => {
        // Only the first final wins. cancel() flips stopRequested too,
        // but we route cancel through ``close()`` which never triggers
        // a final, so this branch only fires on a legitimate ``stop()``.
        console.debug(
          "[dictation] onFinal — text=%o truncated=%s",
          text, truncated,
        );
        onFinalRef.current?.(text, truncated);
        setStatus("idle");
        setLevels(zeroLevels());
        setElapsedMs(0);
      },
      onError: (msg) => {
        console.warn("[dictation] onError:", msg);
        setError(msg);
        setStatus("error");
        teardown();
      },
      onClose: () => {
        // If we never received a final (cancel, network blip, backend
        // crash), unwind to idle anyway so the UI doesn't get stuck.
        setStatus((s) => (s === "error" ? s : "idle"));
        teardown();
      },
    });
    socketRef.current = socket;

    processor.onaudioprocess = (ev) => {
      const float32 = ev.inputBuffer.getChannelData(0);
      // Down-convert float32 [-1, 1] → int16 LE.
      const int16 = new Int16Array(float32.length);
      for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]!));
        int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      socket.sendPcm(int16.buffer);
    };

    source.connect(processor);
    processor.connect(muteGain);
    muteGain.connect(ctx.destination);
    source.connect(analyser);

    // Level-meter loop. Polls the analyser at 60 Hz but only pushes to
    // React state at LEVEL_TICK_HZ to keep render churn down. Computes
    // RMS over the time-domain buffer for a perceptual loudness curve
    // that maps cleanly to bar height.
    const timeBuf = new Uint8Array(analyser.fftSize);
    const ring: number[] = zeroLevels();
    const tickMs = 1000 / LEVEL_TICK_HZ;
    let lastTick = 0;
    const loop = (now: number) => {
      const a = analyserRef.current;
      if (!a) return;
      if (now - lastTick >= tickMs) {
        lastTick = now;
        a.getByteTimeDomainData(timeBuf);
        let sumSq = 0;
        for (let i = 0; i < timeBuf.length; i++) {
          const v = (timeBuf[i]! - 128) / 128;
          sumSq += v * v;
        }
        // RMS scaled to a perceptual bar height. The √(sumSq/N) on a
        // talking voice typically lands in ~0.05–0.35; map that with a
        // mild ceiling so loud bursts don't peg the bar and quiet
        // speech still draws something visible.
        const rms = Math.sqrt(sumSq / timeBuf.length);
        const level = Math.min(1, Math.pow(rms * 3.2, 0.7));
        ring.shift();
        ring.push(level);
        setLevels([...ring]);
        setElapsedMs(performance.now() - startTimeRef.current);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [status, teardown]);

  // On unmount, kill any in-flight recording — otherwise the mic stays
  // hot if the user navigates away mid-utterance.
  useEffect(() => {
    return () => {
      socketRef.current?.close();
      teardown();
    };
  }, [teardown]);

  return { status, levels, elapsedMs, error, start, stop, cancel };
}
